import { Coins, ShieldCheck, Receipt, Landmark, HandCoins, BusFront } from "lucide-react";
import { Crest, FlagStrip } from "@/components/Brand";

const SETTLEMENT = [
  { role: "Platform Operator", basis: "Fixed GH₵2 per booked seat", color: "text-[#0052FF]" },
  { role: "Tracking Company", basis: "Fixed GH₵3 per booked seat (routed to the Consolidated Fund when no tracker is installed on the bus)", color: "text-indigo-600" },
  { role: "Station Master", basis: "The entire balance of the ticket revenue after the operator and tracking fees", color: "text-orange-600" },
];

function Block({ icon: Icon, title, children, tint = "bg-blue-50 text-[#0052FF]" }) {
  return (
    <section className="card-soft p-5 sm:p-6">
      <div className="flex items-center gap-3">
        <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-2xl ${tint}`}><Icon className="h-5 w-5" /></span>
        <h2 className="font-display text-lg font-extrabold tracking-tight text-slate-900 sm:text-xl">{title}</h2>
      </div>
      <div className="mt-3 space-y-3 text-sm leading-relaxed text-slate-600">{children}</div>
    </section>
  );
}

export default function Operations() {
  return (
    <div data-testid="operations-page">
      <section className="relative overflow-hidden bg-[#0E1A5C] text-white">
        <Crest variant="white" className="pointer-events-none absolute -right-16 -top-10 h-80 w-80 opacity-[0.06]" />
        <FlagStrip />
        <div className="relative mx-auto max-w-5xl px-4 py-12 sm:px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#FCD116]">Internal · Staff handbook</div>
          <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Operations &amp; Cash-Flow Handbook</h1>
          <p className="mt-3 max-w-2xl text-sm text-blue-100">How money moves through the GPRTU platform — from the teller counter to the 3-way bus sign-out settlement, driver pay and the bank. Keep this as the reference for station and cash staff.</p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl space-y-5 px-4 py-10 sm:px-6">
        <Block icon={Coins} title="1. Cash collection at the counter">
          <p>Tellers open a shift with an opening float and sell tickets by cash, Mobile Money or card. Every sale, refund and cancellation fee is recorded against the open shift, so the expected drawer total is always: <b>opening float + cash sales + cash carried in − refunds</b>.</p>
          <p>Online and app sales are collected directly by Mobile Money / card and are tracked separately from the counter drawer.</p>
        </Block>

        <Block icon={Receipt} title="2. Teller reconciliation & shift close-out" tint="bg-blue-50 text-[#0052FF]">
          <p>At the end of a shift the teller declares the cash counted. The system compares it against the expected cash and records any variance. The shift report lists tickets sold, cash / MoMo / card split, refunds and cancellation fees.</p>
          <p>A closed shift is then handed to the cashier for verification.</p>
        </Block>

        <Block icon={HandCoins} title="3. Three-way bus sign-out settlement" tint="bg-orange-50 text-orange-600">
          <p>When a bus is signed out, its ticket revenue (fares only) is split between exactly three beneficiaries:</p>
          <div className="overflow-hidden rounded-xl border border-slate-200">
            <table className="w-full text-left text-sm">
              <thead><tr className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><th className="p-3">Beneficiary</th><th className="p-3">How it is calculated</th></tr></thead>
              <tbody>
                {SETTLEMENT.map((r) => (
                  <tr key={r.role} className="border-t border-slate-100" data-testid={`ops-settlement-${r.role.replace(/\s+/g, "-").toLowerCase()}`}>
                    <td className={`p-3 font-bold ${r.color}`}>{r.role}</td>
                    <td className="p-3 text-slate-600">{r.basis}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="rounded-xl bg-slate-50 p-4">
            <div className="label-xs mb-1">Worked example · 40 seats sold at GH₵50</div>
            <ul className="space-y-1 font-mono text-sm text-slate-700">
              <li>Gross fare revenue — 40 × GH₵50 = <b>GH₵2,000.00</b></li>
              <li>Platform Operator — 40 × GH₵2 = <b>GH₵80.00</b></li>
              <li>Tracking Company — 40 × GH₵3 = <b>GH₵120.00</b></li>
              <li className="text-orange-700">Station Master — 2,000 − 80 − 120 = <b>GH₵1,800.00</b></li>
            </ul>
          </div>
          <p>Sales or cancellations at later stops after a bus is signed out automatically refresh the still-pending split so the figures stay correct until disbursement.</p>
        </Block>

        <Block icon={ShieldCheck} title="4. Insurance fund isolation" tint="bg-emerald-50 text-emerald-600">
          <p>The optional travel-insurance fee (set per seat in Settings) is <b>never</b> included in the three-way split. It is tracked in its own total on every sign-out slip and settled separately with the underwriter. Fares and insurance never mix.</p>
        </Block>

        <Block icon={Landmark} title="5. Cashier handover, vault & bank deposit" tint="bg-emerald-50 text-emerald-600">
          <p>The cashier receives each closed shift, confirming the physical cash against the declared amount and logging any variance. Received cash moves into the station vault.</p>
          <p>From the vault the cashier records bank deposits and authorised withdrawals, and acknowledges cash transfers between tellers and the station. The ledger keeps a running balance for audit.</p>
        </Block>

        <Block icon={BusFront} title="6. Driver pay & owner disbursement" tint="bg-orange-50 text-orange-600">
          <p>Station masters pay drivers from the station cash buffer. Each payout shows the Operator (GH₵2/seat) / Tracking (GH₵3/seat) / Station-master-share breakdown and is capped by the available buffer and the amount due.</p>
          <p>Bus owners receive their settlement from the central account, and can review their fleet's trips, seats sold and payout statements from the Bus Owner portal.</p>
        </Block>

        <p className="text-center text-xs text-slate-400">This handbook describes current platform behaviour and may be updated as processes change. Figures such as the GH₵2 operator and GH₵3 tracking fees are configurable by a super-admin in Settings.</p>
      </section>
    </div>
  );
}
